/**
 * Route & amount section: gateway, TLD, amount, optional schedule.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { DollarSign, Filter, Route } from 'lucide-react';

import { Button, DateTimePicker, Input, SearchableSelect } from '@/components/ui';
import { formatCost } from '@/lib/utils';
import { NO_TAGS_VALUE, TagFilterDropdown } from '@/features/smswiser-campaigns/components/shared';
import {
  scheduleLocalToUtcIso,
  utcIsoToScheduleLocal,
} from '@/features/smswiser-campaigns/utils/scheduleTimezone';
import type { RouteAmountSectionProps } from '../drop-modals.types';
import { useGatewayOptions } from './useGatewayOptions';

const KEEP_EACH_ROUTE = -1;
const KEEP_EACH_TLD = '__keep_each__';

export const RouteAmountSection: FC<RouteAmountSectionProps> = ({
  gateways,
  tlds,
  routeId,
  setRouteId,
  tldValue,
  setTldValue,
  amount,
  setAmount,
  executeAfter,
  setExecuteAfter,
  costInfo,
  isBusy,
  showSchedule = true,
  multiScaleMode = false,
  allowKeepEachGateway = true,
  tagsByGatewayId,
  tagColors,
}) => {
  const { gatewayOptions, gatewayTagFilter, setGatewayTagFilter, availableGatewayTags } =
    useGatewayOptions({ gateways, tagsByGatewayId, tagColors });

  return (
    <section className="space-y-3">
      <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Route className="h-3.5 w-3.5" />
        Route & amount
      </h4>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Gateway</label>
        <SearchableSelect<number>
          options={[
            ...(multiScaleMode && allowKeepEachGateway
              ? [{ value: KEEP_EACH_ROUTE, label: '— Keep each drop\'s gateway —' }]
              : []),
            ...gatewayOptions,
          ]}
          value={routeId}
          onChange={setRouteId}
          placeholder="Select gateway"
          searchPlaceholder="Search gateways..."
          headerSlot={
            availableGatewayTags.length > 0 ? (
              <div className="flex shrink-0 items-center gap-2">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                <TagFilterDropdown
                  value={gatewayTagFilter}
                  options={availableGatewayTags}
                  tagColors={tagColors}
                  displayLabel={
                    gatewayTagFilter === ''
                      ? 'All tags'
                      : gatewayTagFilter === NO_TAGS_VALUE
                        ? 'No tags'
                        : gatewayTagFilter
                  }
                  onSelect={setGatewayTagFilter}
                  placeholder="All tags"
                  compact
                />
              </div>
            ) : undefined
          }
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">TLD</label>
          <select
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            value={multiScaleMode && !tldValue ? KEEP_EACH_TLD : tldValue}
            onChange={(e) => setTldValue(e.target.value === KEEP_EACH_TLD ? '' : e.target.value)}
          >
            {multiScaleMode && <option value={KEEP_EACH_TLD}>— Keep each drop&apos;s TLD —</option>}
            <option value="">{multiScaleMode ? 'Select TLD to apply to all' : 'Select TLD'}</option>
            {tlds.map((t) => (
              <option key={t.value} value={t.value}>{t.displayName}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Amount</label>
          <Input
            type="number"
            min={1}
            className="h-10"
            value={amount === '' ? '' : amount}
            onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            error={amount === '' || (typeof amount === 'number' && amount < 1) ? 'Min 1 required' : undefined}
          />
          {costInfo && (
            <div className="mt-2 flex gap-2 text-xs">
              <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-semibold text-destructive">
                {costInfo.total != null ? formatCost(costInfo.total) : `$${costInfo.perSms.toFixed(3)}/SMS`}
              </span>
            </div>
          )}
        </div>
      </div>
      {showSchedule && (
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            Schedule <span className="text-muted-foreground/80">(optional)</span>
          </label>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <DateTimePicker
                value={executeAfter ? utcIsoToScheduleLocal(executeAfter) : ''}
                onChange={(v) => setExecuteAfter(v ? scheduleLocalToUtcIso(v) : '')}
                placeholder="dd/mm/yyyy, --:--"
                disabled={isBusy}
                minDateToday
                ariaLabel="Schedule drop"
                className="min-w-0 flex-1"
              />
            </div>
            {executeAfter && (
              <Button type="button" variant="outline" size="sm" className="w-fit text-xs" onClick={() => setExecuteAfter('')} disabled={isBusy}>
                Remove schedule
              </Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
};
